import Component from './component';
import Hex from './hex';
import InputEvent from './input-event';

export default class Input extends Component {
	/**
	 * The blurOnEnter property sets if the Input lets go of the focus once Enter or NumpadEnter has been released in it.
	 *
	 * True by default, which suits a field that is done with once Enter is pressed. Set it to false where Enter can be refused: a row that will not save an invalid value wants the user to go on typing in the very same field, and a blur would have sent the focus to the body instead.
	 *
	 * @param {boolean} value True if the Input blurs after Enter or false. default is true
	 */
	public set blurOnEnter(value: boolean) {
		assertIsBoolean(value);
		this.#blurOnEnter = value;
	}

	public get blurOnEnter(): boolean {
		return this.#blurOnEnter;
	}

	/**
	 * The decimals property sets if a numeric Input accepts a decimal point. It has no effect when type is 'text'.
	 *
	 * Set it to false to only accept whole numbers. A typed dot is refused along with the digits typed straight after it, and a pasted or set value is cut at its first dot, so "1.5" becomes "1" and not "15".
	 *
	 * @param {boolean} value True if a decimal point is accepted or false. default is true
	 */
	public set decimals(value: boolean) {
		if (this.#decimals === value) {
			return;
		}
		assertIsBoolean(value);
		this.#decimals = value;
		this.#decimalsChanged = true;
		this.invalidateProperties();
	}

	public get decimals(): boolean {
		return this.#decimals;
	}

	/**
	 * The enabled property sets if the TextInput is interactive or not.
	 *
	 * @param {boolean} value True if the TextInput is interactive or false. default is true
	 */
	public set enabled(value: boolean) {
		if (this.#enabled === value) {
			return;
		}
		assertIsBoolean(value);
		this.#enabled = value;
		this.#enabledChanged = true;
		this.invalidateProperties();
	}

	public get enabled(): boolean {
		return this.#enabled;
	}

	/**
	 * The color CSS property sets the foreground color value of an element's text and text decorations, and sets the currentcolor value. currentcolor may be used as an indirect value on other properties and is the default for other color properties, such as border-color.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/color} for syntax
	 */
	public set fill(value: Hex | null) {
		assertIsValidFill(value);
		this.#fill = value;
		this.#fillChanged = true;
		this.invalidateProperties();
	}

	public get fill(): Hex | null {
		return this.#fill;
	}

	/**
	 * The font-family CSS property specifies a prioritized list of one or more font family names and/or generic family names for the selected element.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-family} for syntax
	 */
	public set fontFamily(value: string) {
		assertIsString(value);
		this.#fontFamily = value;
		this.#fontFamilyChanged = true;
		this.invalidateProperties();
	}

	public get fontFamily(): string {
		return this.#fontFamily;
	}

	/**
	 * The font size in pixels
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-size} for syntax
	 */
	public set fontSize(value: number) {
		assertIsNonNegative(value);
		this.#fontSize = value;
		this.#fontSizeChanged = true;
		this.invalidateProperties();
	}

	public get fontSize(): number {
		return this.#fontSize;
	}

	/**
	 * The font-weight CSS property sets the weight (or boldness) of the font. Valid values are from 1 to 1000.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight} for syntax
	 */
	public set fontWeight(value: number) {
		assertIsFromOneToThousand(value);
		this.#fontWeight = value;
		this.#fontWeightChanged = true;
		this.invalidateProperties();
	}

	public get fontWeight(): number {
		return this.#fontWeight;
	}

	/**
	 * The letter-spacing CSS property sets the horizontal spacing behavior between text text. This value is added to the natural spacing between text while rendering the text. Positive values of letter-spacing causes text to spread farther apart, while negative values of letter-spacing bring text closer together.
	 * Valid values are positive and negative numbers in pixels.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/letter-spacing} for syntax
	 */
	public set letterSpacing(value: number) {
		assertIsNumber(value);
		this.#letterSpacing = value;
		this.#letterSpacingChanged = true;
		this.invalidateProperties();
	}

	public get letterSpacing(): number {
		return this.#letterSpacing;
	}

	/**
	 * The line-height CSS property sets the height of a line box in horizontal writing modes. In vertical writing modes, it sets the width of a line box. It's commonly used to set the distance between lines of text. On block-level elements in horizontal writing modes, it specifies the preferred height of line boxes
	 * within the element, and on non-replaced inline elements, it specifies the height that is used to calculate line box height.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/line-height} for syntax
	 */
	public set lineHeight(value: 'auto' | number) {
		assertIsValidLineHeight(value);
		this.#lineHeight = value;
		this.#lineHeightChanged = true;
		this.invalidateProperties();
	}

	public get lineHeight(): 'auto' | number {
		return this.#lineHeight;
	}

	/**
	 * The maximumCharacters property, sets the maximum allowed characters to be typed or pasted into the Input by the user.
	 *
	 * If you want to "unset" the maximumCharacters, give Number.NaN as the parameter.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/maxLength} for syntax
	 */
	public set maximumCharacters(value: number) {
		if (this.#maximumCharacters === value) {
			return;
		}
		assertIsNonNegativeOrNaN(value);
		this.#maximumCharacters = value;
		this.#maximumCharactersChanged = true;
		this.invalidateProperties();
	}

	public get maximumCharacters(): number {
		return this.#maximumCharacters;
	}

	/**
	 * The negatives property sets if a numeric Input accepts a leading minus sign. It has no effect when type is 'text'.
	 *
	 * @param {boolean} value True if negative numbers are accepted or false. default is false
	 */
	public set negatives(value: boolean) {
		if (this.#negatives === value) {
			return;
		}
		assertIsBoolean(value);
		this.#negatives = value;
		this.#negativesChanged = true;
		this.invalidateProperties();
	}

	public get negatives(): boolean {
		return this.#negatives;
	}

	public set readOnly(value: boolean) {
		if (this.#readOnly === value) {
			return;
		}
		assertIsBoolean(value);
		this.#readOnly = value;
		this.#readOnlyChanged = true;
		this.invalidateProperties();
	}

	public get readOnly(): boolean {
		return this.#readOnly;
	}

	/**
	 * The selectOnFocus property sets if the whole text is selected when the Input takes the focus, so what the user types replaces it rather than being added to it.
	 *
	 * False by default, which leaves the caret where the browser puts it. Set it to true for a field whose value is typed again whole rather than edited — a measurement or a quantity, say — so the user does not have to delete what is there first.
	 *
	 * Selected when the focus arrives and not after: by Tab, by focus() or by a click. Text set while the Input already has the focus is not selected, so a field that keeps the focus after a refused Enter leaves the user where they were. A click is the one way in that takes more than selecting on focus, because WebKit
	 * puts the caret under the pointer once the focus is in, which undoes the selection — so a click that brought the focus selects again when the button is released, unless the user dragged out a selection of their own.
	 *
	 * @param {boolean} value True if the text is selected when the Input takes the focus or false. default is false
	 */
	public set selectOnFocus(value: boolean) {
		assertIsBoolean(value);
		this.#selectOnFocus = value;
	}

	public get selectOnFocus(): boolean {
		return this.#selectOnFocus;
	}

	/**
	 * The slashedZero property is part of the font-feature-settings, that will show the zero with a slash if set to true.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-feature-settings} for syntax
	 */
	public set slashedZero(value: boolean) {
		if (this.#slashedZero === value) {
			return;
		}
		assertIsBoolean(value);
		this.#slashedZero = value;
		this.#slashedZeroChanged = true;
		this.invalidateProperties();
	}

	public get slashedZero(): boolean {
		return this.#slashedZero;
	}

	/**
	 * The tabularFigures is part of the font-feature-settings, that will ensure that numbers has mono space size if set to true.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-feature-settings} for syntax
	 */
	public set tabularFigures(value: boolean) {
		if (this.#tabularFigures === value) {
			return;
		}
		assertIsBoolean(value);
		this.#tabularFigures = value;
		this.#tabularFiguresChanged = true;
		this.invalidateProperties();
	}

	public get tabularFigures(): boolean {
		return this.#tabularFigures;
	}

	/**
	 * The text in the field: what was last set, or what the user has typed since.
	 *
	 * Answered from what the component holds rather than read off the input element, so a value set before the component is in the document reads back as what was set, and not as whatever the element held last. The two never drift: typing writes the element's value here as it comes — see inputListener — and setting
	 * writes it to the element when the properties are committed, which is at once while connected and on connecting otherwise. A numeric field reads back what was set until that commit sanitises it; see #commitTextAndType.
	 */
	public set text(value: string) {
		assertIsString(value);
		this.#text = value;
		this.#textChanged = true;
		this.invalidateProperties();
	}

	public get text(): string {
		return this.#text;
	}

	/**
	 * The text-align CSS property sets the horizontal alignment of the inline-level content inside a block element. This means it works like vertical-align but in the horizontal direction.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/text-align} for syntax
	 */
	public set textAlign(value: 'center' | 'left' | 'right') {
		if (this.#textAlign === value) {
			return;
		}
		assertIsValidTextAlign(value);
		this.#textAlign = value;
		this.#textAlignChanged = true;
		this.invalidateProperties();
	}

	public get textAlign(): 'center' | 'left' | 'right' {
		return this.#textAlign;
	}

	/**
	 * The type property is used when you want to enforce numeric validation for the text property or user input.
	 *
	 * If the type is set to 'numeric', validation of the values will be enforced, otherwise not.
	 */
	public set type(value: 'numeric' | 'text') {
		if (this.#type === value) {
			return;
		}
		assertIsValidType(value);
		this.#type = value;
		this.#typeChanged = true;
		this.invalidateProperties();
	}

	public get type(): 'numeric' | 'text' {
		return this.#type;
	}

	#blurOnEnter: boolean;
	#decimals: boolean;
	#decimalsChanged: boolean;
	#enabled: boolean;
	#enabledChanged: boolean;
	#fill: Hex | null;
	#fillChanged: boolean;
	// a press on the input that is bringing the focus to it, so its release is the click that selectOnFocus answers
	#focusingByPointer: boolean;
	#fontFamily: string;
	#fontFamilyChanged: boolean;
	#fontSize: number;
	#fontSizeChanged: boolean;
	#fontWeight: number;
	#fontWeightChanged: boolean;
	#input!: HTMLInputElement;
	#letterSpacing: number;
	#letterSpacingChanged: boolean;
	#lineHeight: 'auto' | number;
	#lineHeightChanged: boolean;
	#maximumCharacters: number;
	#maximumCharactersChanged: boolean;
	#negatives: boolean;
	#negativesChanged: boolean;
	#readOnly: boolean;
	#readOnlyChanged: boolean;
	#refusingFraction: boolean;
	#selectOnFocus: boolean;
	#slashedZero: boolean;
	#slashedZeroChanged: boolean;
	#tabularFigures: boolean;
	#tabularFiguresChanged: boolean;
	#text: string;
	#textAlign: 'center' | 'left' | 'right';
	#textAlignChanged: boolean;
	#textChanged: boolean;
	#type: 'numeric' | 'text';
	#typeChanged: boolean;

	/**
	 * Moves focus to the input element this wraps.
	 *
	 * A Component is not focusable itself, so without this a caller asking an Input to take focus would be asking a wrapper that cannot hold it, and nothing would happen.
	 */
	public override focus(options?: FocusOptions): void {
		this.input.focus(options);
	}

	private get input(): HTMLInputElement {
		if (!this.#input) {
			this.#input = document.createElement('input');
			this.#input.setAttribute('name', Math.random().toString());
			this.#input.style.backgroundColor = 'transparent';
			this.#input.style.outline = 'none';
			this.#input.style.border = 'none';
			this.#input.style.width = '100%';
			this.#input.style.height = '100%';
			this.#input.style.position = 'absolute';
			this.#input.style.padding = '0px';
			this.#input.style.lineHeight = '32px';
			this.#input.style.fontWeight = '400';
			this.#input.style.fontSize = '16px';
			this.#input.style.color = '';
			this.#input.style.fontFamily = '';
			this.#input.type = 'text';
			this.#input.style.lineHeight = '1.2';
			this.#input.style.textAlign = 'start';
		}
		return this.#input;
	}

	public constructor() {
		super();
		this.#blurOnEnter = true;
		this.#decimals = true;
		this.#decimalsChanged = false;
		this.#enabled = true;
		this.#enabledChanged = false;
		this.#fill = null;
		this.#fillChanged = false;
		this.#focusingByPointer = false;
		this.#fontFamily = '';
		this.#fontFamilyChanged = false;
		this.#fontSize = 16;
		this.#fontSizeChanged = false;
		this.#fontWeight = 400;
		this.#fontWeightChanged = false;
		this.#letterSpacing = 0;
		this.#letterSpacingChanged = false;
		this.#lineHeight = 'auto';
		this.#lineHeightChanged = false;
		this.#maximumCharacters = Number.NaN;
		this.#maximumCharactersChanged = false;
		this.#negatives = false;
		this.#negativesChanged = false;
		this.#readOnly = false;
		this.#readOnlyChanged = false;
		this.#refusingFraction = false;
		this.#selectOnFocus = false;
		this.#slashedZero = false;
		this.#slashedZeroChanged = false;
		this.#tabularFigures = false;
		this.#tabularFiguresChanged = false;
		this.#text = '';
		this.#textChanged = false;
		this.#textAlign = 'left';
		this.#textAlignChanged = false;
		this.#type = 'text';
		this.#typeChanged = false;
		this.height = 32;
		this.width = 32;
		this.#bindEventListeners();
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#textChanged || this.#typeChanged || this.#decimalsChanged || this.#negativesChanged) {
			this.#commitTextAndType();
		}
		if (this.#fillChanged) {
			this.#commitFill();
		}
		this.#checkFontProperties();
		if (this.#slashedZeroChanged || this.#tabularFiguresChanged) {
			this.#commitSlashZeroAndTabularFigures();
		}
	}

	protected enabledChanged(): void {
		// override in sub classes
	}

	protected override initialize(): void {
		super.initialize();
		this.appendChild(this.input);
		this.#addEventListeners();
	}

	#addEventListeners(): void {
		this.input.addEventListener('focus', this.focusListener);
		this.input.addEventListener('blur', this.blurListener);
		this.input.addEventListener('input', this.inputListener);
		this.input.addEventListener('pointerdown', this.pointerDownListener);
		this.input.addEventListener('mouseup', this.mouseUpListener);
	}

	#addKeyboardListener(): void {
		window.addEventListener('keyup', this.keyboardListener);
	}

	#bindEventListeners(): void {
		this.focusListener = this.focusListener.bind(this);
		this.blurListener = this.blurListener.bind(this);
		this.inputListener = this.inputListener.bind(this);
		this.keyboardListener = this.keyboardListener.bind(this);
		this.mouseUpListener = this.mouseUpListener.bind(this);
		this.pointerDownListener = this.pointerDownListener.bind(this);
	}

	#checkFontProperties(): void {
		if (this.#fontFamilyChanged) {
			this.#commitFontFamily();
		}
		if (this.#fontSizeChanged) {
			this.#commitFontSize();
		}
		if (this.#fontWeightChanged) {
			this.#commitFontWeight();
		}
		if (this.#letterSpacingChanged) {
			this.#commitLetterSpacing();
		}
		if (this.#lineHeightChanged) {
			this.#commitLineHeight();
		}
		if (this.#textAlignChanged) {
			this.#commitTextAlign();
		}
		if (this.#enabledChanged) {
			this.#commitEnabled();
		}
		if (this.#maximumCharactersChanged) {
			this.#commitMaximumCharacters();
		}
		if (this.#readOnlyChanged) {
			this.#commitReadOnly();
		}
	}

	#commitEnabled(): void {
		this.#enabledChanged = false;
		if (this.enabled) {
			this.input.disabled = false;
		} else {
			// we call blur() here, so the browser can handle correct focus management
			// when toggling enabled = true/false
			this.input.blur();
			this.input.disabled = true;
		}
		this.enabledChanged();
	}

	#commitFill(): void {
		this.#fillChanged = false;
		if (this.fill instanceof Hex) {
			this.input.style.color = this.fill.toStyleString();
		} else {
			this.input.style.color = '';
		}
	}

	#commitFontFamily(): void {
		this.#fontFamilyChanged = false;
		this.input.style.fontFamily = this.fontFamily;
	}

	#commitFontSize(): void {
		this.#fontSizeChanged = false;
		this.input.style.fontSize = `${this.fontSize}px`;
	}

	#commitFontWeight(): void {
		this.#fontWeightChanged = false;
		this.input.style.fontWeight = this.fontWeight.toString();
	}

	#commitLetterSpacing(): void {
		this.#letterSpacingChanged = false;
		this.input.style.letterSpacing = `${this.letterSpacing}px`;
	}

	#commitLineHeight(): void {
		this.#lineHeightChanged = false;
		if (this.lineHeight === 'auto') {
			this.input.style.lineHeight = '1.2';
			return;
		}
		this.input.style.lineHeight = `${this.lineHeight}px`;
	}

	#commitMaximumCharacters(): void {
		this.#maximumCharactersChanged = false;
		if (Number.isNaN(this.#maximumCharacters)) {
			this.input.setAttribute('maxLength', '');
		} else {
			this.input.setAttribute('maxLength', this.#maximumCharacters.toString());
		}
	}

	#commitReadOnly(): void {
		this.#readOnlyChanged = false;
		this.input.readOnly = this.#readOnly;
		if (this.#readOnly) {
			this.#removeEventListeners();
		} else {
			this.#addEventListeners();
		}
	}

	#commitSlashZeroAndTabularFigures(): void {
		this.#slashedZeroChanged = false;
		this.#tabularFiguresChanged = false;
		if (this.slashedZero === true && this.tabularFigures === true) {
			this.input.style.fontFeatureSettings = '"zero" 1, "tnum" 1';
		} else if (this.slashedZero === true && this.tabularFigures === false) {
			this.input.style.fontFeatureSettings = '"zero" 1';
		} else if (this.slashedZero === false && this.tabularFigures === true) {
			this.input.style.fontFeatureSettings = '"tnum" 1';
		} else {
			this.input.style.fontFeatureSettings = '';
		}
	}

	#commitTextAlign(): void {
		this.#textAlignChanged = false;
		if (this.textAlign === 'left') {
			this.input.style.textAlign = 'start';
		} else if (this.textAlign === 'right') {
			this.input.style.textAlign = 'end';
		} else {
			this.input.style.textAlign = 'center';
		}
	}

	#commitTextAndType(): void {
		this.#textChanged = false;
		this.#typeChanged = false;
		this.#decimalsChanged = false;
		this.#negativesChanged = false;
		this.#refusingFraction = false;
		if (this.type === 'numeric') {
			this.#text = this.#getValidNumericCharacters(this.#text);
		}
		this.input.value = this.#text;
	}

	#getValidNumericCharacters(value: string): string {
		const sign = this.#negatives && value.trimStart().startsWith('-') ? '-' : '';
		// without decimals everything from the first dot is dropped, so "1.5" becomes "1" and not "15"
		const accepted = this.#decimals || !value.includes('.') ? value : value.slice(0, value.indexOf('.'));
		// remove all characters that are not numeric or dots
		const digitsAndDots = accepted.replace(/[^\d.]/g, '');
		const firstDotIndex = digitsAndDots.indexOf('.');
		const integer = firstDotIndex === -1 ? digitsAndDots : digitsAndDots.slice(0, firstDotIndex);
		// leading zeros are dropped but one digit is kept, so "007" becomes "7" and "000" becomes "0"
		const trimmedInteger = integer.replace(/^0+(?=\d)/, '');
		if (firstDotIndex === -1) {
			return `${sign}${trimmedInteger}`;
		}
		// the first dot is kept and the trailing ones removed. Trailing zeros are kept, so "0.05" and "1.50" can be typed
		const fraction = digitsAndDots.slice(firstDotIndex + 1).replaceAll('.', '');
		return `${sign}${trimmedInteger || '0'}.${fraction}`;
	}

	/**
	 * Without decimals a typed decimal point is refused, and so are the digits typed straight after it at the end.
	 *
	 * Sanitising each keystroke on its own cannot tell the 5 in "1.5" from the 5 in "15" — the dot is gone by then — so it would turn "1.5" into 15 and "500.5" into 5005. The fraction is refused until the user does anything other than type another digit at the end.
	 */
	#isRefusedKeystroke(typed: string | null): boolean {
		if (this.#decimals) {
			return false;
		}
		const atEnd = this.input.selectionStart === this.input.value.length;
		// a comma is refused as well, since that is the decimal separator a Danish keyboard types
		if (typed === '.' || typed === ',') {
			this.#refusingFraction = atEnd;
			return true;
		}
		if (this.#refusingFraction && atEnd && typed !== null && /^\d$/.test(typed)) {
			return true;
		}
		this.#refusingFraction = false;
		return false;
	}

	/** Puts back the value from before the keystroke, with the caret where it was. */
	#refuseKeystroke(): void {
		const caret = Math.max((this.input.selectionStart ?? this.#text.length) - 1, 0);
		this.input.value = this.#text;
		this.input.setSelectionRange(caret, caret);
	}

	#removeEventListeners(): void {
		this.input.removeEventListener('focus', this.focusListener);
		this.input.removeEventListener('blur', this.blurListener);
		this.input.removeEventListener('input', this.inputListener);
		this.input.removeEventListener('pointerdown', this.pointerDownListener);
		this.input.removeEventListener('mouseup', this.mouseUpListener);
	}

	#removeKeyboardListener(): void {
		window.removeEventListener('keyup', this.keyboardListener);
	}

	private blurListener(): void {
		this.#focusingByPointer = false;
		this.#refusingFraction = false;
		this.#removeKeyboardListener();
		this.dispatchEvent(new InputEvent(InputEvent.BLURRED, this.text));
	}

	/** Selected before the event, so a listener that reads the selection meets the one the user will type over. */
	private focusListener(): void {
		if (this.#selectOnFocus) {
			this.input.select();
		}
		this.#addKeyboardListener();
		this.dispatchEvent(new InputEvent(InputEvent.FOCUSED, this.text));
	}

	/**
	 * Selects the whole text again as a click that brought the focus is released, for a field that selects on focus.
	 *
	 * WebKit puts the caret under the pointer once the focus is in, so there the selection made on focus is gone by now; Chromium and Firefox keep it, and selecting it again changes nothing — measured in all three rather than assumed. The release is where it is put back, with the default prevented so the browser does
	 * not move the caret once more. A selection the user dragged out is theirs, and is left alone.
	 */
	private mouseUpListener(event: MouseEvent): void {
		if (!this.#focusingByPointer) {
			return;
		}
		this.#focusingByPointer = false;
		const { selectionEnd, selectionStart, value } = this.input;
		const dragged = selectionStart !== selectionEnd && (selectionStart !== 0 || selectionEnd !== value.length);
		if (dragged) {
			return;
		}
		event.preventDefault();
		this.input.select();
	}

	/**
	 * Notes a press that is bringing the focus to the input, so its release can be told from one on a field that already had it.
	 *
	 * Set on every press, so it is never left over: a press on the field that has the focus clears it, a press anywhere else blurs the field, which clears it too, and the user can then place the caret with a click like in any other field.
	 */
	private pointerDownListener(): void {
		this.#focusingByPointer = this.#selectOnFocus && document.activeElement !== this.input;
	}

	private inputListener(event: Event): void {
		if (this.type === 'text') {
			this.#text = this.input.value;
			this.dispatchEvent(new InputEvent(InputEvent.CHANGED, this.text));
			return;
		}
		const typed = event instanceof window.InputEvent && event.inputType === 'insertText' ? event.data : null;
		if (this.#isRefusedKeystroke(typed)) {
			this.#refuseKeystroke();
			return;
		}
		const validValue = this.#getValidNumericCharacters(this.input.value);
		this.input.value = validValue;
		if (this.#text !== validValue) {
			this.#text = validValue;
			this.dispatchEvent(new InputEvent(InputEvent.CHANGED, this.text));
		}
	}

	private keyboardListener(event: KeyboardEvent): void {
		if (event.code === 'Enter' || event.code === 'NumpadEnter') {
			this.dispatchEvent(new InputEvent(InputEvent.ENTER, this.text));
			if (this.#blurOnEnter) {
				this.input.blur();
			}
		}
	}
}
customElements.define('ac-input', Input);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be true or false`);
}

function assertIsFromOneToThousand(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 1 && value <= 1000) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be from 1 to 1000`);
}

function assertIsNonNegative(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number`);
}

function assertIsNonNegativeOrNaN(value: unknown): asserts value is number {
	if (typeof value === 'number' && (value >= 0 || Number.isNaN(value))) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number or Number.NaN`);
}

function assertIsNumber(value: unknown): asserts value is number {
	if (typeof value === 'number') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a number type`);
}

function assertIsString(value: unknown): asserts value is string {
	if (typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string`);
}

function assertIsValidFill(value: unknown): asserts value is Hex | null {
	if (value instanceof Hex || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be null or an instance of Hex`);
}

function assertIsValidLineHeight(value: unknown): asserts value is 'auto' | number {
	if (value === 'auto') {
		return;
	}
	if (typeof value === 'number' && value > 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be more than 0 or "auto"`);
}

function assertIsValidTextAlign(value: unknown): asserts value is 'center' | 'left' | 'right' {
	if (value === 'center' || value === 'left' || value === 'right') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "left", "center" or "right"`);
}

function assertIsValidType(value: unknown): asserts value is 'numeric' | 'text' {
	if (value === 'numeric' || value === 'text') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "numeric" or "text"`);
}
